import apiClient from '../../api/apiClient';

export interface AIChatbot {
  id: number;
  name: string;
  description: string;
  model: string;
  status: string;
  is_active: boolean;
  initial_message: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains: string[];
  general_learning_set: string;
  created_at: string;
  updated_at: string;
  company_id: number;
  widget_key: string;
  widget_settings?: any;
  settings?: any;
  training_status?: string;
  chat_mode?: string;
}

// Eksik interface'leri ekle
export interface AIChatbotCreate {
  name: string;
  description?: string;
  model: string;
  status: string;
  is_active: boolean;
  initial_message?: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains?: string[];
  general_learning_set?: string;
  company_id: number;
  widget_settings?: any;
}

export interface AIChatbotUpdate {
  name?: string;
  description?: string;
  model?: string;
  status?: string;
  is_active?: boolean;
  initial_message?: string;
  module?: string;
  // assistant_definition removed; use settings.assistant_profile.persona
  allowed_domains?: string[];
  general_learning_set?: string;
  company_id?: number;
  widget_settings?: any;
  settings?: any;
  training_status?: string;
  chat_mode?: string;
}

export interface AIChatbotFilters {
  company_id?: number;
  status?: string;
  is_active?: boolean;
  search?: string;
  module?: string;
}

export interface TokenUsage {
  id: number;
  chatbot_id: number;
  session_id: string;
  model_name: string;
  operation_type: string;
  token_count: number;
  created_at: string;
}

const getChatbots = async (params: any): Promise<any> => {
  const data = await apiClient.get('/ai-chatbots', { params });
  // API yanıtı bir dizi ise, istenilen formata dönüştür
  if (Array.isArray(data)) {
    return {
      chatbots: data,
      total: data.length,
    };
  }
  return data;
};

// getChatbot ve getChatbotById aynı şeyi yapıyor, birini diğerine yönlendir
const getChatbot = async (id: number): Promise<AIChatbot> => {
  const data = await apiClient.get(`/ai-chatbots/${id}`);
  return data;
};

const getChatbotById = async (id: number): Promise<AIChatbot> => {
  return getChatbot(id);
};

const createChatbot = async (data: AIChatbotCreate): Promise<AIChatbot> => {
  const res = await apiClient.post('/ai-chatbots', data);
  return res;
};

// updateChatbot fonksiyonunu düzelt, iki parametre alacak şekilde güncelle
const updateChatbot = async (id: number, data: AIChatbotUpdate): Promise<AIChatbot> => {
  const res = await apiClient.put(`/ai-chatbots/${id}`, data);
  return res;
};

const deleteChatbot = async (id: number): Promise<any> => {
  const res = await apiClient.delete(`/ai-chatbots/${id}`);
  return res;
};

const getTokenUsage = async (params: any): Promise<TokenUsage[]> => {
  const data = await apiClient.get(`/ai-chatbots/${params.id}/token-usage`, {
    params: { skip: params.skip, limit: params.limit },
  });
  return data;
};

const getWidgetScript = (chatbot: AIChatbot): string => {
  return `<script>
  (function(w,d,s,o,f,js,fjs){
    w['AI-Chatbot-Widget']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
    w[o].l=1*new Date();js=d.createElement(s);fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','aiChat','https://chat-widget.example.com/loader.js'));
  aiChat('init', { 
    widgetKey: '${chatbot.widget_key}',
    chatbotName: '${chatbot.name}'
  });
</script>`;
};

const sendMessage = async (chatbotId: number, message: string) => {
  try {
    const data = await apiClient.post(`/ai-chatbots/${chatbotId}/test`, { message });
    return data;
  } catch (error) {
    console.error('Error sending message to chatbot:', error);
    throw error;
  }
};

const getConversationsList = async (params: any): Promise<any> => {
  const data = await apiClient.get('/ai-chatbots/conversations/list', { params });
  return data;
};

const getChatbotConversations = async (chatbotId: number, params: any): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/conversations`, { params });
  return data;
};

const getConversationMessages = async (conversationId: number, params: any): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${conversationId}/messages`, { params });
  return data;
};

const aiChatbotsService = {
  getChatbots,
  getChatbot,
  getChatbotById,
  createChatbot,
  updateChatbot,
  deleteChatbot,
  getTokenUsage,
  getWidgetScript,
  sendMessage,
  getConversationsList,
  getChatbotConversations,
  getConversationMessages
};

// Training/Knowledge management (admin)
export const getChatbotSettings = async (chatbotId: number): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/settings`);
  return data;
};

export const updateChatbotSettings = async (chatbotId: number, settings: any): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/settings`, settings);
  return data;
};

export const createKnowledgeSource = async (chatbotId: number, body: { type: string; title?: string; lang?: string; tags?: any; meta?: any; }): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources`, body);
  return data;
};

export const listKnowledgeSources = async (chatbotId: number): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources`);
  return data;
};

export const updateKnowledgeSource = async (
  chatbotId: number,
  sourceId: number,
  body: { title?: string; is_active?: boolean; tags?: any; lang?: string }
): Promise<any> => {
  const data = await apiClient.patch(`/ai-chatbots/${chatbotId}/sources/${sourceId}`, body);
  return data;
};

export const deleteKnowledgeSource = async (
  chatbotId: number,
  sourceId: number
): Promise<boolean> => {
  await apiClient.delete(`/ai-chatbots/${chatbotId}/sources/${sourceId}`);
  return true;
};

export const ingestTextToSource = async (chatbotId: number, sourceId: number, text: string): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/ingest-text`, { text });
  return data;
};

export const uploadSourceFile = async (chatbotId: number, sourceId: number, file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const ingestUploadedFile = async (chatbotId: number, sourceId: number, uploadId: number): Promise<any> => {
  const data = await apiClient.post(`/ai-chatbots/${chatbotId}/sources/${sourceId}/ingest-upload/${uploadId}`);
  return data;
};

export const listSourceChunks = async (chatbotId: number, sourceId: number, params?: { skip?: number; limit?: number }): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/chunks`, { params });
  return data;
};

export const searchPreview = async (chatbotId: number, q: string): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/search/preview`, { params: { q } });
  return data;
};

export const getSourceContent = async (chatbotId: number, sourceId: number, version?: number): Promise<{ version: number; text: string; chunks: number }> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/content`, { params: { version } });
  return data;
};

export const listSourceJobs = async (chatbotId: number, sourceId: number, params?: { skip?: number; limit?: number }): Promise<any[]> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/sources/${sourceId}/jobs`, { params });
  return data;
};

export const getJobDetail = async (chatbotId: number, jobId: number): Promise<any> => {
  const data = await apiClient.get(`/ai-chatbots/${chatbotId}/jobs/${jobId}`);
  return data;
};

export default aiChatbotsService;